import { VAN_MODULES } from '@/features/business-van/vanModules';

// ── Which part each surface belongs to ──────────────────────────────────────
// Pattern C of the parts programme (docs/platform/PARTS_PROGRAMME.md): buying
// a part has a runtime effect. This is the one map both sides read — the
// office (the Smart Dash menu, Spaces, Spotlight and the page guard) and the
// crew app (sidebar, bottom bar, routes, home widgets) — so a tenant's
// dashboard and their crews' dashboards show exactly the parts the tenant
// owns, and nothing else. A surface not listed here belongs to the core
// portal and is always on.
//
// The truth is public.active_module_keys(org) (useActiveModuleKeys). The
// flagship owns the whole platform: it fails safe to ON while that answer is
// loading, and otherwise follows it (a cancelled row switches a part off, as
// the RPC intends). A tenant fails safe to OFF: nothing a tenant has not
// bought is ever shown on a hiccup.

export const CORE_PART = 'core-portal';

// Office pages, by the path the classic menu uses (a ?tab= collapses to its
// page). Longest prefix wins.
export const ADMIN_PATH_PART = {
  '/admin/dashboard/allocations': 'operations',
  '/admin/dashboard/calendars': 'operations',
  '/admin/dashboard/availability-cancellations': 'operations',
  '/admin/dashboard/additional-work': 'operations',
  '/admin/dashboard/uniforms': 'operations',
  '/admin/dashboard/routing': 'routes-pay',
  '/admin/dashboard/logging': 'routes-pay',
  '/admin/dashboard/time-requests': 'routes-pay',
  '/admin/dashboard/invoicing': 'invoicing',
  '/admin/dashboard/invoice-settings': 'invoicing',
  '/admin/dashboard/payment-schedules': 'invoicing',
  '/admin/dashboard/pay-run': 'invoicing',
  '/accounting-hub': 'accounting',
  '/admin/dashboard/accounting': 'accounting',
  '/admin/dashboard/financials': 'accounting',
  '/admin/dashboard/performance': 'performance',
  '/admin/dashboard/analytics': 'performance',
  '/admin/dashboard/ai-insights': 'performance',
  '/admin/dashboard/reports': 'performance',
  '/admin/dashboard/national-insights': 'performance',
  '/admin/dashboard/protocols': 'my-day',
  '/admin/dashboard/field-intelligence': 'field-assist',
  '/admin/dashboard/research-programme': 'field-assist',
  '/admin/dashboard/field-cases': 'field-cases',
  '/admin/dashboard/recruitment': 'recruitment',
  '/admin/dashboard/marketing': 'recruitment',
  '/admin/dashboard/jobs': 'recruitment',
  '/admin/dashboard/terminations': 'terminations',
};

// Pages that are the platform's own, not a tenant's: its health, the demo
// programme, the van lab, the flagship's recruitment site, and the backend
// desks whose tables are not org-shaped (owner, 8 Oct 2026: "admins on
// tenure should not have access to certain backend functionality"):
// activity_logs (every admin's actions, no org column), the engine room's
// AI spend (ai_usage_log) and the platform's webhook configs. A tenant never
// sees them, whatever they own. Data Tools stays, cut down to their own mail
// (DataToolsPage).
export const FLAGSHIP_ONLY_PATHS = [
  '/admin/dashboard/platform-health',
  '/admin/dashboard/product-demos',
  '/admin/dashboard/business-van-lab',
  '/admin/dashboard/activity',
  '/admin/dashboard/engine-room',
  '/admin/dashboard/webhooks',
  'https://drivers.parcelink.org/management',
  // Parcelink's own driver-supply partners and business agreements (MARBE).
  // Owner, 9 Oct: tenants have no reason to see them — gated for good.
  '/admin/dashboard/partnerships',
  // Where Parcelink ranks against the other carriers on its client's
  // scoreboard (client_scorecard_report is behind the flagship gate,
  // migration 20261336). A tenant has no scoreboard here.
  '/admin/dashboard/performance/network',
  // Owner, 19 Oct 2026 (a tenant's Comms Space showed the parcelink.org
  // inboxes): "The MAIL component is for parcelink portal admin only."
  // The mailbox mirror is org-scoped at the data layer, but the page is
  // Parcelink's (its aliases are written into it).
  '/admin/dashboard/mail',
  // Parcelink's own growth desks, benchmarks and presentation material:
  // the marketing hub (its landing pages, SEO engine, media and B2B
  // pipeline), the job pages on drivers.parcelink.org, the national
  // benchmarks from its client's reports, the AI desk that directs the
  // platform's own agents over every table, the sales decks and the
  // training hub. None of it is a tenant's, whatever parts they own.
  '/admin/dashboard/marketing',
  '/admin/dashboard/jobs',
  '/admin/dashboard/national-insights',
  '/admin/dashboard/ai-insights',
  '/admin/dashboard/decks',
  'https://logisticshub.learn.link',
];

// The crew app. What a tenant's subcontractors see is what the tenant bought.
export const CREW_PATH_PART = {
  '/subcontractor/dashboard/availability': 'operations',
  '/subcontractor/dashboard/uniform': 'operations',
  '/subcontractor/dashboard/logs': 'routes-pay',
  '/subcontractor/dashboard/routing': 'routes-pay',
  '/subcontractor/dashboard/live-invoice': 'invoicing',
  '/subcontractor/dashboard/performance': 'performance',
  '/subcontractor/dashboard/my-day': 'my-day',
  '/subcontractor/dashboard/field-assist': 'field-assist',
  '/subcontractor/dashboard/cases': 'field-cases',
  '/subcontractor/dashboard/my-coach': 'my-coach',
};

const ALL_PATH_PART = { ...ADMIN_PATH_PART, ...CREW_PATH_PART };

const base = (path) => String(path || '').split('?')[0];

/** The part a path belongs to, or null for a core surface. Longest prefix wins. */
export function partForPath(path, map = ALL_PATH_PART) {
  const p = base(path);
  let best = null;
  for (const [prefix, part] of Object.entries(map)) {
    if (p === prefix || p.startsWith(`${prefix}/`)) {
      if (!best || prefix.length > best.prefix.length) best = { prefix, part };
    }
  }
  return best ? best.part : null;
}

export const isFlagshipOnly = (path) => FLAGSHIP_ONLY_PATHS.some((p) => base(path) === p || base(path).startsWith(`${p}/`));

/**
 * Whether a surface is on for this org.
 *   'on'      show it
 *   'off'     the tenant does not own the part (or the page is the platform's)
 *   'loading' a tenant whose parts have not answered yet: hide, never flash
 * @param {string} path
 * @param {{ keys: string[]|null, loaded: boolean, isFlagship: boolean }} access
 */
export function partAccess(path, access, map = ALL_PATH_PART) {
  const { keys, loaded, isFlagship } = access || {};
  if (!isFlagship && isFlagshipOnly(path)) return 'off';
  const part = partForPath(path, map);
  if (!part) return 'on';
  if (isFlagship) return loaded && Array.isArray(keys) ? (keys.includes(part) ? 'on' : 'off') : 'on';
  if (!loaded) return 'loading';
  return Array.isArray(keys) && keys.includes(part) ? 'on' : 'off';
}

export const allowedByParts = (path, access, map) => partAccess(path, access, map) === 'on';

/** The classic menu, minus every item of a part this org does not own. */
export function filterGroupsByParts(groups, access) {
  return groups
    .map((g) => ({ ...g, items: (g.items || []).filter((it) => partAccess(it.path, access, ADMIN_PATH_PART) === 'on') }))
    .filter((g) => g.items.length > 0);
}

// ── The cart: a part never arrives without what it runs on ──────────────────
// The hard edges are the van's (vanModules.js): invoicing needs routes-pay,
// routes-pay needs operations, and so on. Adding a part adds its chain.
export const REQUIRES = Object.fromEntries(VAN_MODULES.map((m) => [m.key, m.requires || []]));

/** Every key in `keys` plus everything they require, transitively, in a stable order. */
export function closure(keys, requires = REQUIRES) {
  const out = [];
  const visit = (k) => {
    if (out.includes(k)) return;
    for (const r of requires[k] || []) visit(r);
    out.push(k);
  };
  for (const k of keys || []) visit(k);
  return out;
}

/** The active keys that would be left without `key` — the ones that require it. */
export function dependents(key, activeKeys, requires = REQUIRES) {
  return (activeKeys || []).filter((k) => k !== key && closure([k], requires).includes(key));
}
